import { PRACTICE_TIME_ZONE } from '@tebuto/functions/practice-time'

const WEEKDAY_ADVERBS: Record<string, string> = {
    Montag: 'montags',
    Dienstag: 'dienstags',
    Mittwoch: 'mittwochs',
    Donnerstag: 'donnerstags',
    Freitag: 'freitags',
    Samstag: 'samstags',
    Sonntag: 'sonntags'
}

/** Monday = 0 … Sunday = 6 (German week order). */
const WEEKDAY_ORDER: Record<string, number> = {
    Montag: 0,
    Dienstag: 1,
    Mittwoch: 2,
    Donnerstag: 3,
    Freitag: 4,
    Samstag: 5,
    Sonntag: 6
}

function weekdayName(date: Date): string {
    return new Intl.DateTimeFormat('de-DE', { weekday: 'long', timeZone: PRACTICE_TIME_ZONE }).format(date)
}

function clockTime(date: Date): string {
    return new Intl.DateTimeFormat('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: PRACTICE_TIME_ZONE
    }).format(date)
}

function dateLabel(date: Date): string {
    return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: PRACTICE_TIME_ZONE
    }).format(date)
}

export type SessionLike = { start: string; end: string }

export function formatSessionDate(iso: string): string {
    return dateLabel(new Date(iso))
}

export type SessionDateTimeParts = {
    weekday: string
    date: string
    timeRange: string
}

export function formatSessionDateTimeParts(start: string, end: string): SessionDateTimeParts | null {
    const startDate = new Date(start)
    const endDate = new Date(end)
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return null
    }

    return {
        weekday: new Intl.DateTimeFormat('de-DE', { weekday: 'short', timeZone: PRACTICE_TIME_ZONE }).format(startDate),
        date: dateLabel(startDate),
        timeRange: `${clockTime(startDate)}–${clockTime(endDate)} Uhr`
    }
}

export type SessionSchedule =
    | { kind: 'single'; date: string; startTime: string; endTime: string }
    | { kind: 'recurring'; count: number; weekdayAdverb: string; startTime: string; endTime: string }
    | { kind: 'irregular'; count: number; firstDate: string; lastDate: string }

/**
 * There is no forced regularity for seminar sessions — they can be created with
 * arbitrary dates/times. Only claim a recurring "Tuesdays 18:00-20:00" pattern
 * when every session actually shares the same weekday and clock time; otherwise
 * fall back to a plain count + date range rather than asserting a false pattern.
 */
export function describeSessionSchedule(sessions: SessionLike[]): SessionSchedule | null {
    if (sessions.length === 0) {
        return null
    }

    const sorted = [...sessions].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
    const parsed = sorted.map((session) => ({ start: new Date(session.start), end: new Date(session.end) }))

    if (parsed.length === 1) {
        const { start, end } = parsed[0]
        return { kind: 'single', date: dateLabel(start), startTime: clockTime(start), endTime: clockTime(end) }
    }

    const firstWeekday = weekdayName(parsed[0].start)
    const firstStartTime = clockTime(parsed[0].start)
    const firstEndTime = clockTime(parsed[0].end)

    const isRegular = parsed.every(
        ({ start, end }) =>
            weekdayName(start) === firstWeekday &&
            clockTime(start) === firstStartTime &&
            clockTime(end) === firstEndTime
    )

    if (isRegular) {
        return {
            kind: 'recurring',
            count: parsed.length,
            weekdayAdverb: WEEKDAY_ADVERBS[firstWeekday] ?? firstWeekday,
            startTime: firstStartTime,
            endTime: firstEndTime
        }
    }

    return {
        kind: 'irregular',
        count: parsed.length,
        firstDate: dateLabel(parsed[0].start),
        lastDate: dateLabel(parsed[parsed.length - 1].end)
    }
}

export function formatSessionSchedule(
    schedule: SessionSchedule | null,
    options?: { includeDateRange?: boolean }
): string {
    if (!schedule) {
        return ''
    }
    switch (schedule.kind) {
        case 'single':
            return `am ${schedule.date} von ${schedule.startTime}–${schedule.endTime} Uhr`
        case 'recurring':
            return `${schedule.count} Termine · ${schedule.weekdayAdverb} ${schedule.startTime}–${schedule.endTime}`
        case 'irregular':
            if (options?.includeDateRange === false) {
                return `${schedule.count} Termine`
            }
            return `${schedule.count} Termine · ${schedule.firstDate} - ${schedule.lastDate}`
    }
}

export function occurrenceTitle(occurrence: { label?: string | null; sessions: SessionLike[] }): string {
    return occurrence.label?.trim() || formatSessionMonthRange(occurrence.sessions) || 'Termin'
}

function monthAndYear(date: Date): { month: string; year: string } {
    return {
        month: new Intl.DateTimeFormat('de-DE', { month: 'long', timeZone: PRACTICE_TIME_ZONE }).format(date),
        year: new Intl.DateTimeFormat('de-DE', { year: 'numeric', timeZone: PRACTICE_TIME_ZONE }).format(date)
    }
}

export type SeminarStartMonth = { month: string; year: string }

/** Start month badge for seminar list cards — e.g. { month: "August", year: "2026" }, based on the earliest session. */
export function formatSeminarStartMonth(sessions: SessionLike[]): SeminarStartMonth | null {
    if (sessions.length === 0) {
        return null
    }

    const first = sessions.reduce<Date | null>((earliest, session) => {
        const start = new Date(session.start)
        if (Number.isNaN(start.getTime())) {
            return earliest
        }
        return earliest === null || start < earliest ? start : earliest
    }, null)

    return first ? monthAndYear(first) : null
}

export function formatSessionMonthRange(sessions: SessionLike[]): string {
    if (sessions.length === 0) {
        return ''
    }

    const sorted = [...sessions].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
    const first = new Date(sorted[0].start)
    const last = new Date(sorted[sorted.length - 1].start)
    if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) {
        return ''
    }

    const start = monthAndYear(first)
    const end = monthAndYear(last)

    if (start.month === end.month && start.year === end.year) {
        return `${start.month} ${start.year}`
    }
    if (start.year === end.year) {
        return `${start.month} - ${end.month} ${start.year}`
    }
    return `${start.month} ${start.year} - ${end.month} ${end.year}`
}

function capitalizeWeekdayAdverb(weekday: string): string {
    const adverb = WEEKDAY_ADVERBS[weekday] ?? weekday
    return adverb.charAt(0).toUpperCase() + adverb.slice(1)
}

export type RegistrationEligibility = {
    canRegister: boolean
    reason: 'ok' | 'status' | 'not_yet_open' | 'closed' | 'full'
}

/**
 * Matches backend `isRegistrationWindowOpen` plus seat availability.
 * Public `status` is the derived lifecycle: registration stays open during `running`
 * when `registrationClosesAt` (or first session start) is still in the future.
 */
export function getRegistrationEligibility(occurrence: {
    status: string
    registrationOpensAt?: string | null
    registrationClosesAt?: string | null
    remainingSeats: number
    waitlistEnabled: boolean
    sessions?: SessionLike[]
}): RegistrationEligibility {
    if (occurrence.status === 'registration_pending') {
        return { canRegister: false, reason: 'not_yet_open' }
    }

    if (
        occurrence.status !== 'registration_open' &&
        occurrence.status !== 'running' &&
        occurrence.status !== 'registration_closed'
    ) {
        return { canRegister: false, reason: 'status' }
    }

    const now = Date.now()
    if (occurrence.registrationOpensAt) {
        const opensAt = new Date(occurrence.registrationOpensAt).getTime()
        if (!Number.isNaN(opensAt) && now < opensAt) {
            return { canRegister: false, reason: 'not_yet_open' }
        }
    }

    const closesAt = resolveRegistrationClosesAt(occurrence)
    if (closesAt != null && now > closesAt) {
        return { canRegister: false, reason: 'closed' }
    }

    if (occurrence.status === 'registration_closed') {
        return { canRegister: false, reason: 'closed' }
    }

    if (occurrence.remainingSeats <= 0 && !occurrence.waitlistEnabled) {
        return { canRegister: false, reason: 'full' }
    }

    return { canRegister: true, reason: 'ok' }
}

function resolveRegistrationClosesAt(occurrence: {
    registrationClosesAt?: string | null
    sessions?: SessionLike[]
}): number | null {
    if (occurrence.registrationClosesAt) {
        const closesAt = new Date(occurrence.registrationClosesAt).getTime()
        return Number.isNaN(closesAt) ? null : closesAt
    }

    const sessions = occurrence.sessions ?? []
    let firstStart: number | null = null
    for (const session of sessions) {
        const start = new Date(session.start).getTime()
        if (Number.isNaN(start)) {
            continue
        }
        if (firstStart == null || start < firstStart) {
            firstStart = start
        }
    }
    return firstStart
}

export function formatRegistrationOpensAt(iso: string | null | undefined): string {
    if (!iso) {
        return ''
    }
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) {
        return ''
    }
    return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        timeZone: PRACTICE_TIME_ZONE
    }).format(date)
}

/**
 * Up to two distinct weekday adverbs from session starts (German week order),
 * e.g. "Sonntags" or "Montags & Donnerstags". Returns null when there are none
 * or more than two distinct weekdays (too irregular to claim a pattern).
 */
export function formatSessionWeekdayAdverbs(sessions: SessionLike[]): string | null {
    const sorted = [...sessions].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
    const weekdays: string[] = []
    const seen = new Set<string>()
    for (const session of sorted) {
        const start = new Date(session.start)
        if (Number.isNaN(start.getTime())) {
            continue
        }
        const weekday = weekdayName(start)
        if (!seen.has(weekday)) {
            seen.add(weekday)
            weekdays.push(weekday)
        }
        if (weekdays.length > 2) {
            return null
        }
    }

    if (weekdays.length === 0) {
        return null
    }

    weekdays.sort((a, b) => (WEEKDAY_ORDER[a] ?? 99) - (WEEKDAY_ORDER[b] ?? 99))
    const adverbs = weekdays.map(capitalizeWeekdayAdverb)
    return adverbs.length === 1 ? adverbs[0] : `${adverbs[0]} & ${adverbs[1]}`
}

/** Hero badge: weekday adverbs (when clear) plus month range. */
export function formatHeroDateBadge(sessions: SessionLike[]): string {
    const monthRange = formatSessionMonthRange(sessions)
    if (!monthRange) {
        return ''
    }

    const weekdayPart = formatSessionWeekdayAdverbs(sessions)
    return weekdayPart ? `${weekdayPart}, ${monthRange}` : monthRange
}
